import { Component, Input, OnInit, ViewChild, EventEmitter, Output } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { LoginService } from '../../services/login.service';
import { AddressService } from '../../services/address.service';
import { RefDataService } from '../../services/ref-data.service';
import { RefDataItem, RefDataType } from '../../data-objects/ref-data-item';
import { RefDataDropdownComponent } from '../ref-data-dropdown/ref-data-dropdown.component';
import { PostalAddress } from '../../data-objects/postal-address';
import { LocationDetails } from '../../data-objects/location-details';
import { suburbs } from './assets/suburbs';


const postcodeRE = /^(0[289][0-9]{2})|([1345689][0-9]{3})|(2[0-8][0-9]{2})|(290[0-9])|(291[0-4])|(7[0-4][0-9]{2})|(7[8-9][0-9]{2})$/;

@Component({
  selector: 'app-postal-address',
  templateUrl: './postal-address.component.html',
  styleUrls: ['./postal-address.component.css']
})
export class PostalAddressComponent implements OnInit {

  @Input()
  locationDetails: LocationDetails;
  @Input()
  isReadOnly: boolean;
  @Input()
  isVisible: boolean = true;
  @Input()
  addressDetails: PostalAddress;
  @ViewChild('streetType')
  streetTypes: RefDataDropdownComponent;
  @ViewChild('streetSuffixType')
  streetSuffixTypes: RefDataDropdownComponent;
  @Input()
  showSameAsApplicantLink: boolean = true;
  @Input()
  showInlineErrorMsgs: boolean = true;
  @Input()
  applicantAddress: any = null;

  @Output()
  emitStructuredPostal = new EventEmitter();

  errorMessage: string;
  unableToParseApplicantDetails: boolean = false;

  poBoxControlLength = 20;
  streetNumberControlLength = 10;
  unitNumberControlLength = 10;
  streetNameControlLength = 50;
  suburbControlLength = 50;
  postcodeControlLength = 4;

  poBoxControl: FormControl;
  unitNumberControl: FormControl;
  streetNumberControl: FormControl;
  streetNameControl: FormControl;
  suburbControl: FormControl;
  postcodeControl: FormControl;

  addressGroup: FormGroup;

  addressTypeControl: FormControl;

  public suburbsAndPostcodes: string[] = [];
  public list: { suburbSuffix: string, suburbs: any[] } = { suburbSuffix: 'Australia', suburbs };

  //#REGION: INITIALIZERS
  constructor(private loginService: LoginService, private refDataService: RefDataService, private addressService: AddressService) {
    this.streetNumberControl = new FormControl('', [Validators.maxLength(this.streetNumberControlLength)]);
    this.unitNumberControl = new FormControl('', [Validators.maxLength(this.unitNumberControlLength)]);
    this.streetNameControl = new FormControl('', [Validators.maxLength(this.streetNameControlLength)]);
    this.suburbControl = new FormControl('', [Validators.maxLength(this.suburbControlLength)]);
    this.poBoxControl = new FormControl('', [Validators.maxLength(this.poBoxControlLength)]);
    this.postcodeControl = new FormControl('', [Validators.compose([
      Validators.maxLength(this.postcodeControlLength),
      Validators.pattern(postcodeRE),
      Validators.required
    ])]);
    this.addressTypeControl = new FormControl('', [ Validators.compose([Validators.required]) ]);

    this.addressGroup = new FormGroup({
      poBox: this.poBoxControl,
      unitNumber: this.unitNumberControl,
      streetNumber: this.streetNumberControl,
      streetName: this.streetNameControl,
      suburb: this.suburbControl,
      postcode: this.postcodeControl
    }, (g: FormGroup): ValidationErrors => {
      return {
        incomplete: !g.controls.postcode.value || !g.controls.suburb.value ||
          (!g.controls.poBox.value && (!g.controls.streetNumber.value || !g.controls.streetName.value || !g.controls.suburb.value))
      };
    });
  }

  ngOnInit() {
    const pleaseSelect: RefDataItem[] = [new RefDataItem(0, 'Please Select...')];
    this.refDataService.retrieveReferenceData(RefDataType.StreetTypes, this.loginService.getToken()).subscribe(
      (refDataItems) => {
        if (this.streetTypes) {
          this.streetTypes.items = pleaseSelect.concat(refDataItems);
          if (this.addressDetails) {
            this.addressDetails.streetTypeId = this.streetTypes.selectedItemId =
              (this.addressDetails.streetTypeId) ? this.addressDetails.streetTypeId : 0;
          }
        }
      },
      err => {
        this.errorMessage = 'Server error';
      }
    );
    const streetSuffix: RefDataItem[] = [new RefDataItem(0, 'Please Select...')];
    this.refDataService.retrieveReferenceData(RefDataType.StreetSuffixes, this.loginService.getToken()).subscribe(
      (refDataItems) => {
        if (this.streetSuffixTypes) {
          this.streetSuffixTypes.items = streetSuffix.concat(refDataItems);
          if (this.addressDetails) {
            this.addressDetails.streetSuffixTypeId = this.streetSuffixTypes.selectedItemId =
              (this.addressDetails.streetSuffixTypeId) ? this.addressDetails.streetSuffixTypeId : 0;
          }
        }
      },
      err => {
        this.errorMessage = 'Server error';
      }
    );
  }

  //#REGION: PUBLIC METHODS
  displayErrorMessage(): string {

    if (!this.isValid) {
      if (this.addressDetails.addressType === 'pobox') {
        this.errorMessage = `Please either provide PO Box, Postcode and Suburb<br>`;
      }
      if (this.addressDetails.addressType === 'residential') {
        this.errorMessage = `Please either provide Street Number, Street Name, Suburb and Postcode<br>`;
      }
      // this.errorMessage = 'Please either provide PO Box, Postcode and Suburb or Street Number, Street Name, Suburb and Postcode<br>';
    } else {
      this.errorMessage = '';
    }
    return this.errorMessage;
  }

  sameAsLocation() {
    if (this.locationDetails) {
      this.addressDetails.copyAddressDetail(this.locationDetails);
      this.streetTypes.selectedItem = new RefDataItem(this.locationDetails.streetTypeId, '');
      this.addressDetails.streetType = this.locationDetails.streetType;
      this.addressDetails.streetTypeId = this.locationDetails.streetTypeId;
      this.addressDetails.streetSuffixType = this.locationDetails.streetSuffixType;
      this.addressDetails.streetSuffixTypeId = this.locationDetails.streetSuffixTypeId;
    }
  }

  sameAsApplicant() {
    if (this.applicantAddress) {
      this.refDataService.retrieveReferenceData(RefDataType.StreetTypes, this.loginService.getToken()).subscribe(
        (refDataItems: any[]) => {
          if (refDataItems && refDataItems.length > 0) {

            this.addressDetails = this.addressService.buildStructuredPostalFromPostalAddress(this.applicantAddress);

            this.addressDetails.addressType = (this.addressDetails.poBox) ? 'pobox' : 'residential';

            this.addressDetails.streetType = this.addressDetails.streetType;
            this.addressDetails.streetSuffixType = this.addressDetails.streetSuffixType;

            const streetTypeId = ((refDataItems || []).filter(r => r.display === this.addressDetails.streetType)[0] || {})['id'] || 0;
            this.addressDetails.streetTypeId = streetTypeId;
            this.streetTypes.selectedItem = new RefDataItem(streetTypeId, '');

            const streetSuffixTypeId = ((refDataItems || []).filter(r => r.display === this.addressDetails.streetSuffixType)[0] || {})['id'] || 0;
            this.addressDetails.streetSuffixTypeId = streetSuffixTypeId;
            this.streetSuffixTypes.selectedItem = new RefDataItem(streetSuffixTypeId, '');

            if (  (!this.addressDetails.poBox && !this.addressDetails.suburb && !this.addressDetails.postCode)
                  || (!this.addressDetails.streetNumber && !this.addressDetails.street && !this.addressDetails.suburb && !this.addressDetails.postCode)
            ) {
              this.unableToParseApplicantDetails = true;
            }

            this.emitStructuredPostal.emit(this.addressDetails);
          }
        },
        err => {
          this.unableToParseApplicantDetails = true;
        });
    }
  }

  closeModal() {
    this.unableToParseApplicantDetails = false;
  }

  markValidationControlsAsUntouched() {
    this.poBoxControl.markAsUntouched();
    this.unitNumberControl.markAsUntouched();
    this.streetNumberControl.markAsUntouched();
    this.streetNameControl.markAsUntouched();
    this.suburbControl.markAsUntouched();
    this.postcodeControl.markAsUntouched();
  }

  handleStreetTypeChanged($event) {
    const selectedItem: RefDataItem = this.streetTypes.selectedItem;
    if (selectedItem) {
      this.addressDetails.streetType = selectedItem.display;
      if (typeof (this.streetTypes.selectedItemId) === 'number') {
        this.addressDetails.streetTypeId = (this.streetTypes.selectedItemId) as number;
      } else {
        this.addressDetails.streetTypeId = Number.parseInt(this.streetTypes.selectedItemId);
      }
    }
  }

  handlestreetSuffixTypeChanged($event) {
    const selectedItem: RefDataItem = this.streetSuffixTypes.selectedItem;
    if (selectedItem) {
      this.addressDetails.streetSuffixType = selectedItem.display;
      if (typeof (this.streetSuffixTypes.selectedItemId) === 'number') {
        this.addressDetails.streetSuffixTypeId = (this.streetSuffixTypes.selectedItemId) as number;
      } else {
        this.addressDetails.streetSuffixTypeId = Number.parseInt(this.streetSuffixTypes.selectedItemId);
      }
    }
  }

  showSuburbPostcodeFields() {
    return ['pobox','residential'].indexOf(this.addressDetails.addressType) > -1;
  }

  searchSuburbOrPostcode(event: any) {
    this.suburbsAndPostcodes = (this.list.suburbs || [])
                                .map(item => ({ suburb: item.suburb, state: item.state, postcode: item.postcode }))
                                .filter(item => {
                                  return ((item.suburb || '').toLowerCase().indexOf((event.query || '').toLowerCase()) > -1 ||
                                    (`${(item.postcode || '')}`).indexOf(event.query || '') > -1)
                                })
                                .map(result => `${result.suburb}, ${result.postcode}, ${result.state}, ${this.list.suburbSuffix}`);
  }

  selectAutocomplete(value: string) {
    const arrVal: string[] = value.split(',');
    const suburb = (arrVal[0] || '').trim();
    const postcode = (arrVal[1] || '').trim();

    this.addressDetails.suburb = suburb;
    this.postCode = postcode;
  }

  //#REGION GET SET VARS
  get postCode() {
    return this.addressDetails ? this.addressDetails.postCode : '';
  }

  set postCode(v: string) {
    if (this.addressDetails) {
      this.addressDetails.postCode = v;
      this.addressDetails.state = this.addressService.stateFromPostcode(v);
    }
  }

  get isValid(): boolean {
    return this.addressDetails
            && !!this.addressDetails.postCode && !!this.addressDetails.suburb
            && ['', undefined, null].indexOf(this.addressDetails.addressType) === -1
            && (!!this.addressDetails.poBox ||
              !!this.addressDetails.streetNumber && !!this.addressDetails.street && !!this.addressDetails.suburb);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/postal-address/postal-address.component.ts